/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from 'react'
import { Col, Form, Row, Button, Card, Dropdown } from 'react-bootstrap'
import PrescriptionDetails from './PrescriptionDetails'
import { getFullName } from '@utils/fullname.util'
import {
  usePracticePatients,
  usePracticeProviderList,
} from '@hooks/usePracticePatients'

interface PrescriptionDetail {
  procedureType: string
  region: string
  material: string
  shadeNo: string
  instructions: string
}

interface Attachment {
  file: File
  url: string
  name: string
  date: string
  size: string
}

interface AddOrderProps {
  formData: any
  onChangeForm: (formData: any) => void
  onToggleNoteModal: (show: boolean) => void
}

const AddOrder: React.FC<AddOrderProps> = ({
  formData,
  onChangeForm,
  onToggleNoteModal,
}) => {
  const [patientName, setPatientName] = useState<string>(
    formData.patientName || '',
  )
  const [labWorkType, setLabWorkType] = useState<string>(
    formData.labWorkType || '',
  )
  const [providerName, setProviderName] = useState<string>(
    formData.providerName || '',
  )
  const [partnerLabName, setPartnerLabName] = useState<string>(
    formData.partnerLabName || '',
  )
  const [dueDate, setDueDate] = useState<string>(formData.dueDate || '')
  const [priority, setPriority] = useState<'Normal' | 'Urgent'>(
    formData.priority || 'Normal',
  )
  const [prescriptionDetails, setPrescriptionDetails] = useState<
    PrescriptionDetail[]
  >(formData?.prescriptionDetails || [])
  const [attachments, setAttachments] = useState<Attachment[]>(
    formData?.attachments || [],
  )
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const patients = usePracticePatients()
  const providers = usePracticeProviderList()

  useEffect(() => {
    onChangeForm({
      patientName,
      labWorkType,
      providerName,
      partnerLabName,
      dueDate,
      priority,
      prescriptionDetails,
      attachments,
    })
  }, [
    patientName,
    labWorkType,
    providerName,
    partnerLabName,
    dueDate,
    priority,
    prescriptionDetails,
    attachments,
    onChangeForm,
  ])

  const handleAddItem = (): void => {
    setPrescriptionDetails((prev) => [
      ...prev,
      {
        procedureType: '',
        region: '',
        material: '',
        shadeNo: '',
        instructions: '',
      },
    ])
    setEditingRowIndex(prescriptionDetails.length)
  }

  const handleEdit = (index: number): void => setEditingRowIndex(index)

  const handleDelete = (index: number): void => {
    setPrescriptionDetails((prev) => prev.filter((_, idx) => idx !== index))
    setEditingRowIndex(null)
  }

  const handleSave = (): void => setEditingRowIndex(null)

  const handleInputChange = (
    index: number,
    field: keyof PrescriptionDetail,
    value: string,
  ): void => {
    const updatedDetails = [...prescriptionDetails]
    updatedDetails[index][field] = value
    setPrescriptionDetails(updatedDetails)
  }

  const handleUploadClick = (): void => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files
    if (files) {
      const fileList = Array.from(files).map((file) => ({
        file,
        url: URL.createObjectURL(file),
        name: file.name,
        date: new Date(file.lastModified).toLocaleDateString(),
        size: `${(file.size / 1024).toFixed(2)} KB`,
      }))
      setAttachments((prev) => [...prev, ...fileList])
    }
  }

  const handleDeleteAttachment = (index: number): void => {
    URL.revokeObjectURL(attachments[index].url)
    setAttachments((prev) => prev.filter((_, idx) => idx !== index))
  }

  return (
    <div className="p-0 add-order-form">
      <Form>
        {/* Header Form */}
        <Row className="mb-2 gx-2">
          <Col>
            <Form.Label>Patient</Form.Label>
            <Form.Select
              value={patientName}
              onChange={(e) => setPatientName(e.target.value)}
            >
              <option value="">Select patient</option>
              {patients.map((patient) => (
                <option key={patient.patient_id} value={getFullName(patient)}>
                  {getFullName(patient)}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col>
            <Form.Label>Lab work type</Form.Label>
            <Form.Select
              value={labWorkType}
              onChange={(e) => setLabWorkType(e.target.value)}
            >
              <option value="">Select work type</option>
              <option value="Prosthodontics">Prosthodontics</option>
              <option value="Orthodontics">Orthodontics</option>
              <option value="Implantology">Implantology</option>
              <option value="Periodontics">Periodontics</option>
            </Form.Select>
          </Col>
        </Row>

        <Row className="mb-2 gx-2">
          <Col>
            <Form.Label>Provider</Form.Label>
            <Form.Select
              value={providerName}
              onChange={(e) => setProviderName(e.target.value)}
            >
              <option value="">Select provider</option>
              {providers.map((provider) => (
                <option
                  key={provider.provider_id}
                  value={getFullName(provider)}
                >
                  {getFullName(provider)}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col>
            <Form.Label>Partner lab</Form.Label>
            <Form.Select
              value={partnerLabName}
              onChange={(e) => setPartnerLabName(e.target.value)}
            >
              <option value="">Select lab</option>
              <option value="Smile Labs">Smile Labs</option>
              <option value="OralCare Lab">OralCare Lab</option>
            </Form.Select>
          </Col>
        </Row>

        <Row className="mb-2 gx-2">
          <Col>
            <Form.Label>Due date</Form.Label>
            <Form.Control
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </Col>
          <Col>
            <Form.Label>Set priority</Form.Label>
            <Form.Select
              value={priority}
              onChange={(e) =>
                setPriority(e.target.value as 'Normal' | 'Urgent')
              }
            >
              <option value="Normal">Normal</option>
              <option value="Urgent">Urgent</option>
            </Form.Select>
          </Col>
        </Row>

        {/* Prescription details Table */}
        <PrescriptionDetails
          prescriptionDetails={prescriptionDetails}
          editingRowIndex={editingRowIndex}
          onAddItem={handleAddItem}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onSave={handleSave}
          onInputChange={handleInputChange}
          onToggleNoteModal={onToggleNoteModal}
        />

        {/* Attachments Section */}
        <Row className="align-items-center mt-4 mb-2">
          <Col>
            <h6 className="mb-0">Attachments</h6>
          </Col>
          <Col className="text-end">
            <Button size="sm" variant="secondary" onClick={handleUploadClick}>
              Upload
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              multiple
              onChange={handleFileChange}
            />
          </Col>
        </Row>

        <Row className="mt-2 gx-2">
          {attachments.length === 0 ? (
            <Col>
              <div className="text-muted text-sm">
                No Attachment(s) Available
              </div>
            </Col>
          ) : (
            attachments.map((att, idx) => (
              <Col key={idx} xs={6} md={4} lg={3} className="mb-3">
                <Card>
                  {att.file.type.startsWith('image/') ? (
                    <Card.Img
                      variant="top"
                      src={att.url}
                      style={{ height: '120px', objectFit: 'cover' }}
                    />
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{ height: '120px' }}
                    >
                      <i
                        className="mgc mgc_file_2_fill"
                        style={{ fontSize: '48px' }}
                      />
                    </div>
                  )}
                  <Card.Body className="p-2">
                    <div className="d-flex justify-content-between align-items-start">
                      <Card.Title className="fs-6 text-truncate mb-0">
                        {att.name}
                      </Card.Title>
                      <Dropdown>
                        <Dropdown.Toggle
                          variant="link"
                          bsPrefix="p-0 m-0"
                          style={{ fontSize: '18px' }}
                        >
                          <i className="mgc mgc_more_2_fill" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => handleDeleteAttachment(idx)}
                          >
                            <i className="mgc mgc_delete_2_line pe-1" /> Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                    <Card.Text className="text-muted small mt-1">
                      {att.date} • {att.size}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))
          )}
        </Row>
      </Form>
    </div>
  )
}

export default AddOrder
